import { apiFetch } from './api-client';

interface SkuRunRow {
  id: string;
  skuCode: string;
  output: unknown;
  createdAt: string;
}

async function fetchLatestRun(jwt: string, skuCode: string): Promise<SkuRunRow | null> {
  try {
    const res = await apiFetch(
      `/api/v1/sku-runs?skuCode=${encodeURIComponent(skuCode)}&limit=1`,
      jwt,
    );
    if (!res.ok) return null;
    const data = (await res.json()) as { runs?: SkuRunRow[] };
    return data.runs?.[0] ?? null;
  } catch {
    return null;
  }
}

function extractIds(run: SkuRunRow): { packIds: string[]; runIds: string[] } {
  const out = (run.output ?? {}) as { packIds?: unknown; runIds?: unknown };
  const packIds = Array.isArray(out.packIds)
    ? out.packIds.filter((x): x is string => typeof x === 'string')
    : [];
  const runIds = Array.isArray(out.runIds)
    ? out.runIds.filter((x): x is string => typeof x === 'string')
    : [];
  return { packIds, runIds };
}

/**
 * Capture the newest SkuRun id for this sku BEFORE starting a purchase. Discovery
 * then waits for a run whose id differs from this baseline — telling the freshly
 * created run apart from a prior one WITHOUT comparing client and server clocks
 * (a skewed client clock previously made time-window discovery miss the run).
 *
 * Returns null if the user has no prior run for this sku (then any run is "new").
 */
export async function getBaselineSkuRunId(jwt: string, skuCode: string): Promise<string | null> {
  const run = await fetchLatestRun(jwt, skuCode);
  return run?.id ?? null;
}

/**
 * Real Telegram Stars flow: after openInvoice() resolves 'paid', the bot's
 * `successful_payment` webhook dispatches the job and writes a SkuRun
 * (output = { packIds } for avatar, { runIds } for past-lives) ASYNCHRONOUSLY.
 * The Mini App can't get the id synchronously, so it polls GET /api/v1/sku-runs
 * until the newest run differs from `baselineRunId` and carries ids.
 *
 * `baselineRunId` is the newest run id captured before purchase (see
 * getBaselineSkuRunId). The timeout is purely local elapsed time — no clock
 * comparison — so device clock skew cannot break discovery.
 *
 * Returns null on timeout (webhook never produced a new run).
 */
export async function discoverSkuRunIds(
  jwt: string,
  skuCode: string,
  baselineRunId: string | null,
  opts: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<{ packIds: string[]; runIds: string[] } | null> {
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const intervalMs = opts.intervalMs ?? 2_000;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const run = await fetchLatestRun(jwt, skuCode);
    if (run && run.id !== baselineRunId) {
      const ids = extractIds(run);
      if (ids.packIds.length || ids.runIds.length) return ids;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}
